import React from 'react';
import pluralize from 'pluralize';
import styled from 'astroturf/react';
import { TextButton } from 'js/design_system/TextButton';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Dropdown } from 'js/design_system/forms/Dropdown';
import trackClick from 'js/utils/trackClick';
import commaizeInt from 'js/utils/commaize';
import { Filter } from '../common';

const SettingsLink = styled(TextButton)`
  font-size: 14px;
  color: var(--text-primary, #141413);
  justify-content: flex-start;
`;

const Header = styled.div`
  max-width: 856px; // Matches .mentions-card-container--cards
  margin: 8px auto 12px auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  @media only screen and (max-width: 887px) {
    // 887px matches$FilterContainer-breakpoint in mentions_filter.scss
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const HeaderInfo = styled.div`
  flex: 1;
  @media only screen and (max-width: 887px) {
    // 887px matches $FilterContainer-breakpoint in mentions_filter.scss
    gap: 4px;
  }
`;

const SettingsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// Wraps Dropdown so we can override the inner button margin
const SortDropdownWrapper = styled.div`
  & button {
    margin: 0;
  }
`;

interface Props {
  publicMentions?: boolean;
  counts?: Record<string, number> | null;
  currentFilter: Filter;
  names?: string[];
  mentionedUserUrl?: string;
  sortBy?: 'recency' | 'relevance';
  onSortChange?: (sortBy: 'recency' | 'relevance') => void;
}

function getMentionedNames(
  names: string[],
  mentionedUserUrl?: string,
): React.ReactNode {
  if (names.length === 0) {
    return 'you';
  }
  if (mentionedUserUrl !== undefined) {
    return (
      <BodyLink href={mentionedUserUrl}>
        <strong className="u-tcBlue">{names[0]}</strong>
      </BodyLink>
    );
  }
  return names.flatMap((name, i) =>
    i === 0
      ? [<strong key={i}>{name}</strong>]
      : [<span key={`or-${i}`}> or </span>, <strong key={i}>{name}</strong>],
  );
}

export function MentionsCardHeader({
  publicMentions,
  counts = {},
  currentFilter,
  names,
  mentionedUserUrl,
  sortBy,
  onSortChange,
}: Props): React.ReactElement {
  const count = counts?.[currentFilter] ?? null;
  const showHeaderText = !!(
    names &&
    currentFilter !== 'not_me' &&
    count != null &&
    count > 0
  );

  const sortButton = onSortChange ? (
    <SortDropdownWrapper>
      <Dropdown<'relevance' | 'recency'>
        value={sortBy ?? 'relevance'}
        options={['relevance', 'recency']}
        renderOption={(option) =>
          option === 'relevance' ? 'Relevance' : 'Recency'
        }
        renderValue={(value) =>
          `Sort by: ${value === 'relevance' ? 'Relevance' : 'Recency'}`
        }
        onChange={(newSortBy) => {
          trackClick({
            source: 'mentions_card_header.sort',
            extra: { sort_by: newSortBy, filter: currentFilter },
          });
          onSortChange(newSortBy);
        }}
      />
    </SortDropdownWrapper>
  ) : null;

  return (
    <Header>
      {!publicMentions && (
        <SettingsRow>
          <SettingsLink
            icon="settings"
            size="sm"
            inline={true}
            trackClick={{
              source: 'mentions_card_header.settings',
            }}
            onClick={() => {
              window.location.href = '/settings';
            }}
          >
            Name Settings
          </SettingsLink>
          {!showHeaderText && sortButton}
        </SettingsRow>
      )}
      {(showHeaderText || publicMentions) && (
        <HeaderRow>
          <HeaderInfo>
            {showHeaderText ? (
              <p
                className="ds2-5-body-lg"
                style={{ color: 'var(--text-primary, #141413)' }}
              >
                <strong>
                  {`${commaizeInt(count as number)} ${pluralize('papers', count as number)}`}
                </strong>
                {count === 1 ? ' mentions "' : ' mention "'}
                {getMentionedNames(names ?? [], mentionedUserUrl)}
                {'"'}
              </p>
            ) : (
              <span>&nbsp;</span>
            )}
          </HeaderInfo>
          {sortButton}
        </HeaderRow>
      )}
    </Header>
  );
}
